import React, { createContext, useContext, useEffect, useState } from 'react';

import { useAppDispatch, useTypedSelector } from '../store';
import { setLoading, setTenant } from '../store/tenantSlice';
import { addBreadcrumb } from '../utils/errorReporting';
import {
	getTenantId,
	getTenantIdentifier,
	setTenantId,
} from '../utils/tenantUtils';

interface TenantContextValue {
	tenant: any | null;
	loading: boolean;
	error: string | null;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export const useTenant = () => {
	const context = useContext(TenantContext);
	if (context === undefined) {
		throw new Error('useTenant must be used within a TenantProvider');
	}
	return context;
};

interface TenantProviderProps {
	children: React.ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
	const dispatch = useAppDispatch();
	const tenant = useTypedSelector((state) => state.tenant.tenant);
	const tenantLoading = useTypedSelector((state) => state.tenant.loading);
	const [error, setError] = useState<string | null>(null);
	const [isInitialized, setIsInitialized] = useState(false);

	useEffect(() => {
		const initializeTenant = async () => {
			try {
				// Check if tenant_id already in localStorage
				const existingTenantId = getTenantId();

				// Get tenant identifier from domain or URL
				const { tenantId, subdomain } = getTenantIdentifier();

				// If we have a tenant identifier from domain/URL, always refresh tenant data
				// This ensures tenant config is loaded even if localStorage has stale data
				if (existingTenantId && !tenantId && !subdomain) {
					// No new tenant identifier found, and we already have one in storage
					// Mark as initialized and skip API call
					setIsInitialized(true);
					return;
				}

				if (!tenantId && !subdomain) {
					// No tenant identifier found, mark as initialized (no tenant)
					setIsInitialized(true);
					return;
				}

				// Set loading state
				dispatch(setLoading(true));

				// Get API URL
				const currentApiUrl =
					process.env.REACT_APP_STATEMENT_API ||
					new URLSearchParams(window.location.search).get('apiUrl') ||
					'http://localhost:5006';

				const baseUrl = currentApiUrl.endsWith('/api')
					? currentApiUrl
					: `${currentApiUrl}/api`;

				// Call public tenant config API
				let response: Response;
				if (tenantId) {
					response = await fetch(
						`${baseUrl}/tenant/config?tenant_id=${encodeURIComponent(tenantId)}`,
					);
				} else if (subdomain) {
					response = await fetch(
						`${baseUrl}/tenant/config?subdomain=${encodeURIComponent(subdomain)}`,
					);
				} else {
					setIsInitialized(true);
					return;
				}

				if (response.ok) {
					const data = await response.json();
					if (data.success && data.tenant) {
						// Store tenant_id in localStorage
						setTenantId(data.tenant.id);
						// Store full tenant data in Redux (this also sets loading to false)
						dispatch(setTenant(data.tenant));
						addBreadcrumb(
							`Tenant initialized: ${data.tenant.name} (${data.tenant.id})`,
							'tenant',
							'info',
						);
						setIsInitialized(true);
					} else {
						// Tenant not found - set loading to false
						dispatch(setLoading(false));
						setError('Tenant not found');
						setIsInitialized(true);
					}
				} else {
					// Tenant not found or error - log but don't fail
					console.warn('Failed to load tenant config:', response.status);
					dispatch(setLoading(false));
					setError(`Failed to load tenant config: ${response.status}`);
					setIsInitialized(true);
				}
			} catch (err) {
				// Non-fatal error - log but don't fail app initialization
				console.error('Error initializing tenant:', err);
				addBreadcrumb('Tenant initialization failed', 'tenant', 'warning');
				dispatch(setLoading(false));
				setError(err instanceof Error ? err.message : 'Unknown error');
				setIsInitialized(true);
			}
		};

		initializeTenant();
	}, [dispatch]);

	// Don't render children until tenant initialization is complete
	// This ensures all API calls have tenant context available
	if (!isInitialized) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
					<p className="mt-4 text-gray-600">Loading...</p>
				</div>
			</div>
		);
	}

	const value: TenantContextValue = {
		tenant,
		loading: tenantLoading,
		error,
	};

	return (
		<TenantContext.Provider value={value}>{children}</TenantContext.Provider>
	);
};
